import classnames from "classnames";

import { ButtonProps } from "fe-shared/src/components";
import {
  CloseIcon,
  ReturnIcon,
  CheckIcon,
  EditIcon,
} from "fe-shared/src/components/Icons";
import { PermissionLevel, UserPermission } from "fe-shared/src/types/domain";
import {
  Transaction,
  TransactionStatus,
  TransactionType,
} from "fe-shared-transactions/src/types";
import { RevertIcon } from "fe-shared-transactions/src/components/Icons";

import { TransactionAction } from "types";
import { PERMISSIONS, TRANSACTION_ADJUSTMENT_APPLICABLE_TYPES } from "consts";

import styles from "../TransactionDetailsView.module.scss";

const {
  Invalidate,
  Restore,
  Return,
  Approve,
  CreateAdjustment,
  EditAdjustment,
  ApproveAdjustment,
  EditDisposition,
} = TransactionAction;

export interface ActionConfig {
  name: TransactionAction;
  isVisible: (
    transaction: Transaction,
    isCurrentUserAssignee: boolean
  ) => boolean;
  isDisabled?: (transaction: Transaction) => boolean;
  hasConfirmationDialog: boolean;
  buttonProps: Partial<ButtonProps>;
  dialogButtonProps?: Partial<ButtonProps>;
  permission: (type?: TransactionType) => UserPermission | UserPermission[];
}

const { InReview, Invalid, Approved } = TransactionStatus;

export const actions: ActionConfig[] = [
  {
    name: Approve,
    isVisible: (t, isCurrentUserAssignee) =>
      isCurrentUserAssignee &&
      t.statusCode === InReview &&
      t.transactionType !== TransactionType.Adjustment,
    hasConfirmationDialog: false,
    buttonProps: {
      variant: "filled",
      iconComponent: CheckIcon,
    },
    dialogButtonProps: {
      variant: "filled",
    },
    permission: () => [
      {
        name: PERMISSIONS.transactionApproveFirstLevel,
        level: PermissionLevel.Full,
      },
      {
        name: PERMISSIONS.transactionApproveSecondLevel,
        level: PermissionLevel.Full,
      },
    ],
  },
  {
    name: ApproveAdjustment,
    isVisible: (t, isCurrentUserAssignee) =>
      isCurrentUserAssignee &&
      t.statusCode === InReview &&
      t.isInClosurePeriod &&
      t.transactionType === TransactionType.Adjustment,
    isDisabled: (t) =>
      !t.sourceWarehouse?.isActive || !t.targetWarehouse?.isActive,
    hasConfirmationDialog: false,
    buttonProps: {
      variant: "filled",
      iconComponent: CheckIcon,
    },
    dialogButtonProps: {
      variant: "filled",
    },
    permission: () => [
      {
        name: PERMISSIONS.transactionAdjustmentApproveFirstLevel,
        level: PermissionLevel.Full,
      },
      {
        name: PERMISSIONS.transactionAdjustmentApproveSecondLevel,
        level: PermissionLevel.Full,
      },
    ],
  },
  {
    name: Return,
    isVisible: (t, isCurrentUserAssignee) =>
      isCurrentUserAssignee &&
      t.statusCode === InReview &&
      ![
        TransactionType.Adjustment,
        TransactionType.Disposition,
        TransactionType.Retreading,
      ].includes(t.transactionType),
    hasConfirmationDialog: true,
    buttonProps: {
      variant: "outlined",
      iconComponent: ReturnIcon,
    },
    dialogButtonProps: {
      variant: "filled",
    },
    isDisabled: (t) =>
      !t.transactionFlags.some((f) => f.isVisibleToParticipant),
    permission: () => ({
      name: PERMISSIONS.transactionReturn,
      level: PermissionLevel.Full,
    }),
  },
  {
    name: Restore,
    isVisible: (t) =>
      t.statusCode === Invalid &&
      ![TransactionType.Disposition, TransactionType.Retreading].includes(
        t.transactionType
      ) &&
      t.isInClosurePeriod &&
      !(
        t.transactionType === TransactionType.Adjustment &&
        !t.invalidatedBy?.userId
      ) &&
      !(
        t.transactionType === TransactionType.Delivery &&
        !t.linkedTransactions.some(
          (lt) => lt.transactionType === TransactionType.Receipt
        )
      ),
    hasConfirmationDialog: false,
    buttonProps: {
      iconComponent: RevertIcon,
    },
    permission: (type) =>
      type === TransactionType.Adjustment
        ? {
            name: PERMISSIONS.transactionAdjustmentRestore,
            level: PermissionLevel.Full,
          }
        : {
            name: PERMISSIONS.transactionRevert,
            level: PermissionLevel.Full,
          },
  },
  {
    name: Invalidate,
    isVisible: (t, isCurrentUserAssignee) =>
      isCurrentUserAssignee && t.statusCode === InReview && t.isInClosurePeriod,
    hasConfirmationDialog: true,
    buttonProps: {
      iconComponent: CloseIcon,
      className: styles.invalidateButton,
    },
    dialogButtonProps: {
      className: classnames(
        styles.invalidateButton,
        styles.invalidateButtonDialog
      ),
    },
    permission: (type) =>
      type === TransactionType.Adjustment
        ? {
            name: PERMISSIONS.transactionAdjustmentInvalidate,
            level: PermissionLevel.Full,
          }
        : {
            name: PERMISSIONS.transactionInvalidation,
            level: PermissionLevel.Full,
          },
  },
  {
    name: CreateAdjustment,
    isVisible: (t) =>
      t.statusCode === Approved &&
      TRANSACTION_ADJUSTMENT_APPLICABLE_TYPES.includes(t.transactionType) &&
      t.isInClosurePeriod &&
      t.linkedTransactions.every(
        (transaction) =>
          transaction.transactionType !== TransactionType.Adjustment
      ),
    isDisabled: (t) =>
      !t.sourceWarehouse?.isActive || !t.targetWarehouse?.isActive,
    hasConfirmationDialog: true,
    buttonProps: {
      variant: "filled",
      iconComponent: EditIcon,
    },
    permission: () => ({
      name: PERMISSIONS.transactionAdjustmentCreate,
      level: PermissionLevel.Full,
    }),
  },
  {
    name: EditAdjustment,
    isVisible: (t, isCurrentUserAssignee) =>
      isCurrentUserAssignee &&
      t.statusCode === InReview &&
      t.isInClosurePeriod &&
      t.transactionType === TransactionType.Adjustment,
    hasConfirmationDialog: true,
    buttonProps: {
      variant: "filled",
      iconComponent: EditIcon,
    },
    permission: () => ({
      name: PERMISSIONS.transactionAdjustmentCreate,
      level: PermissionLevel.Full,
    }),
  },
  {
    name: EditDisposition,
    isVisible: (t, isCurrentUserAssignee) =>
      isCurrentUserAssignee &&
      t.statusCode === InReview &&
      t.transactionType === TransactionType.Disposition,
    hasConfirmationDialog: true,
    buttonProps: {
      variant: "filled",
      iconComponent: EditIcon,
    },
    permission: () => ({
      name: PERMISSIONS.transactionDispositionEdit,
      level: PermissionLevel.Full,
    }),
  },
];
